// 빠진 항목 제안을 편집기에 담는 순수 조각 — 추가 액션과 견적 계열별 무시 목록(localStorage).

import { MissingSuggestion } from "../missing-rules";
import { Quote } from "../types";
import { EditorHistory, isLastStepFrom } from "./history";
import { nextKey } from "./reducer";
import { EditorAction, EditorState, FilledItemInput } from "./types";
import { TradeGroup } from "../../template/trades";

export interface MissingTradeDef {
  code: string;
  name: string;
  group: TradeGroup;
  sort_order: number;
}

const IGNORED_KEY = "buildpy.missing-ignored.";

const itemOf = (suggestion: MissingSuggestion): FilledItemInput => ({
  key: nextKey(),
  space_code: "",
  scope: "",
  item: suggestion.suggestion.name,
  unit: suggestion.suggestion.unit,
  qty: suggestion.suggestion.qty ?? 0,
  qty_expr: suggestion.suggestion.qty_expr ?? null,
  material_unit_price: 0,
  labor_unit_price: 0,
  note: "",
});

// 제안을 담을 공종 — 이미 있는 공종이 먼저, 없으면 고를 수 있는 공종 목록에서.
const missingDefOf = (
  state: Pick<EditorState, "trades">,
  suggestion: MissingSuggestion,
  defs: readonly MissingTradeDef[]
): MissingTradeDef | undefined => {
  const existing = state.trades.find((t) => t.trade === suggestion.trade_code);
  return existing
    ? { code: existing.trade, name: existing.name, group: existing.group, sort_order: existing.sort_order }
    : defs.find((d) => d.code === suggestion.trade_code);
};

// 담을 수 있는가 — 렌더마다 불려도 줄 키(nextKey)를 쓰지 않는다.
export const missingAddable = (
  state: Pick<EditorState, "trades">,
  suggestion: MissingSuggestion,
  defs: readonly MissingTradeDef[]
): boolean => missingDefOf(state, suggestion, defs) !== undefined;

/** 제안 한 줄을 그 공종 끝에 담는 액션(되돌리기 한 단계). 공종이 없으면 새로 세우고, 고를 수 없는 공종이면 null. */
export const missingAddAction = (
  state: Pick<EditorState, "trades">,
  suggestion: MissingSuggestion,
  defs: readonly MissingTradeDef[]
): Extract<EditorAction, { type: "IMPORT_TRADES" }> | null => {
  const def = missingDefOf(state, suggestion, defs);
  if (!def) return null;
  return {
    type: "IMPORT_TRADES",
    trades: [
      {
        trade: def.code,
        name: def.name,
        group: def.group,
        sort_order: def.sort_order,
        items: [itemOf(suggestion)],
      },
    ],
  };
};

/** 액션이 넣을 줄의 키 — 토스트 「되돌리기」 가 그 줄이 아직 있는지 키로 본다. */
export const addedKeyOf = (action: Extract<EditorAction, { type: "IMPORT_TRADES" }>): string | null =>
  action.trades[0]?.items[0]?.key ?? null;

/** 제안으로 넣은 줄이 지금 없는가(⌘Z 로 이미 되돌렸거나 지움) — 이름이 아니라 넣은 줄의 키로 본다. */
export const addedRowGone = (now: Pick<EditorState, "trades">, key: string | null): boolean =>
  key === null ||
  !now.trades.some((t) => t.items.some((i) => i.key === key || i.children.some((c) => c.key === key)));

// 토스트 「되돌리기」 판정 — replaced 는 그 사이 이어쓰기(RESTORE)로 편집이 통째로 갈려 키로는 가를 수 없는 경우.
export type MissingUndo = "undo" | "gone" | "edited" | "replaced";

export const missingUndoOf = (
  history: EditorHistory,
  from: EditorState,
  key: string | null,
  replaced: boolean
): MissingUndo => {
  if (replaced) return "replaced";
  if (isLastStepFrom(history, from)) return "undo";
  return addedRowGone(history.present, key) ? "gone" : "edited";
};

/** 무시 목록의 키. 버전끼리 같은 `quote_no` 를 써서 새 버전에서도 무시가 이어진다. */
export const ignoredKeyOf = (quote: Pick<Quote, "org_id" | "quote_no">): string =>
  `${quote.org_id}:${quote.quote_no}`;

/** `legacyKey` 는 예전(견적 id) 키 — 계열 키가 비었으면 그것을 읽는다. */
export const loadIgnored = (key: string, legacyKey?: string): string[] => {
  try {
    const raw =
      window.localStorage.getItem(IGNORED_KEY + key) ??
      (legacyKey ? window.localStorage.getItem(IGNORED_KEY + legacyKey) : null);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
};

export const saveIgnored = (key: string, ids: readonly string[]): void => {
  try {
    // 빈 목록도 적어 둔다 — 지우면 다음에 예전 키의 목록이 되살아난다.
    window.localStorage.setItem(IGNORED_KEY + key, JSON.stringify(ids));
  } catch {
    // 저장 공간이 막혀도 이번 화면에서는 무시가 유지된다.
  }
};
